import Icon from "../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AddIcon,
  AddUsersIcon,
  Badge,
  Button,
  FlexContent,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
  useDebounce,
} from "@rothoblaas/design-system";
import Header from "../../components/ui/Header";
import Table from "../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../utils/rowActions";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { getProjectStatusValues, SearchAdminProject } from "../../http/types/projects";
import { useProjectsApi } from "../../http/useProjects";
import { PaginationQuery } from "../../types";
import AdminProjectModal from "./AdminProjectModal";
import ProjectSharingDialog from "./ProjectSharingDialog";

const AdminProjects: FC = () => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const [ProjectToDelete, setProjectToDelete] = useState<SearchAdminProject>();
  const [projectToShare, setProjectToShare] = useState<SearchAdminProject>();
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const projectsApi = useProjectsApi();
  const navigate = useNavigate();

  const stati = getProjectStatusValues(i18n.translation.common);

  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = projectsApi.useAdminProjects(query, aborter.current.signal);

  const isInitialized = useInitializer(data || error);

  if (!data && !error && !isInitialized) {
    return;
  }

  return (
    <>
      <Header
        title={i18n.translation.projects.plural}
        path={{ display: i18n.translation.projects.all }}
        buttons={[
          <Button onClick={() => setShowCreateProject(true)}><Icon glyph={AddIcon} />
            {i18n.translation.common.createNew}
          </Button>,
        ]}
      />

      <FlexContent padding noPaddingTop>
        <Table<SearchAdminProject>
          filters={filters}
          search={{
            placeholder: i18n.translation.projects.search,
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.status}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.location}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.designer}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.customer}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.owner}</TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </TableRow>
          }
          items={data?.items}
          renderRow={(project) => {
            const status = stati.find((x) => x.value === project.status);
            return (
              <>
                <TableCell>
                  <Link onClick={(e) => e.stopPropagation()} to={project.id}>
                    {project.name}
                  </Link>
                </TableCell>
                <TableCell>{status && <Badge color={status.color}>{status.display}</Badge>}</TableCell>
                <TableCell>{project.location}</TableCell>
                <TableCell>{project.designer}</TableCell>
                <TableCell>{project.customer}</TableCell>
                <TableCell>
                  {" "}
                  {project.owner?.firstName} {project.owner?.lastName}
                </TableCell>
              </>
            );
          }}
          rowActions={(project) => [
            getEditAction(i18n, () => navigate(`/admin/projects/${project.id}`)),
            {
              label: i18n.translation.projects.editSharings,
              onClick: () => setProjectToShare(project),
              icon: <Icon glyph={AddUsersIcon} />,
            },
            getDeleteAction(i18n, () => setProjectToDelete(project), true),
          ]}
          onRowClick={(project) => navigate(`/admin/projects/${project.id}`)}
          total={data?.total}
          error={!!error}
        />
        {!!projectToShare && (
          <ProjectSharingDialog
            projectId={projectToShare.id}
            projectName={projectToShare.name}
            isAdmin
            onClose={() => setProjectToShare(undefined)}
          />
        )}
        {!!ProjectToDelete && (
          <ModalDialog
            onClose={() => setProjectToDelete(undefined)}
            title={i18n.translation.projects.delete}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setProjectToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  projectsApi
                    .deleteAdminProject(ProjectToDelete.id)
                    .then(() => setProjectToDelete(undefined))
                    .then(() => dispatch(showSuccessNotification(i18n.translation)))
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.projects.questions.delete}
          </ModalDialog>
        )}

        {!!showCreateProject && (
          <AdminProjectModal
            onClose={() => setShowCreateProject(false)}
            onSuccess={() => setShowCreateProject(false)}
          />
        )}
      </FlexContent>
    </>
  );
};

export default AdminProjects;
